import { lazy, type ComponentType, type LazyExoticComponent } from 'react'

const ASSET_RECOVERY_STORAGE_KEY = 'cargonice-asset-recovery'
const ASSET_RECOVERY_COOLDOWN_MS = 30_000
const RECOVERABLE_ASSET_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
  /Unable to preload CSS/i,
  /ChunkLoadError/i,
]

let preloadRecoveryInstalled = false
const LAZY_MODULE_DEFAULT_EXPORT_ERROR = 'Lazy-loaded module resolved without a default export'

type AssetRecoveryMarker = {
  release: string
  path: string
  at: number
}

type AssetRecoveryOptions = {
  force?: boolean
  now?: () => number
  reload?: () => void
}

type VitePreloadErrorEvent = Event & {
  payload?: unknown
}

type AssetRecoveryStateOptions = {
  now?: () => number
}

const getWindowRelease = () => {
  if (typeof window === 'undefined') {
    return ''
  }
  const candidate = (window as Window & { SENTRY_RELEASE?: { id?: string } }).SENTRY_RELEASE?.id
  return typeof candidate === 'string' ? candidate.trim() : ''
}

const getCurrentRelease = () => {
  const envRelease = String(import.meta.env.VITE_SENTRY_RELEASE || '').trim()
  return envRelease || getWindowRelease() || 'unknown'
}

const normalizeErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message || error.name || ''
  }
  if (typeof error === 'string') {
    return error
  }
  if (error && typeof error === 'object') {
    const candidate = error as { message?: unknown; reason?: unknown; payload?: unknown }
    if (typeof candidate.message === 'string' && candidate.message.trim()) {
      return candidate.message
    }
    if (typeof candidate.reason === 'string' && candidate.reason.trim()) {
      return candidate.reason
    }
    if (candidate.payload !== undefined && candidate.payload !== error) {
      return normalizeErrorMessage(candidate.payload)
    }
  }
  return ''
}

export const isRecoverableAssetError = (error: unknown) => {
  const message = normalizeErrorMessage(error)
  if (!message) {
    return false
  }
  return RECOVERABLE_ASSET_ERROR_PATTERNS.some((pattern) => pattern.test(message))
}

const readRecoveryMarker = (): AssetRecoveryMarker | null => {
  if (typeof window === 'undefined') {
    return null
  }
  try {
    const raw = window.sessionStorage.getItem(ASSET_RECOVERY_STORAGE_KEY)
    if (!raw) {
      return null
    }
    const parsed = JSON.parse(raw) as Partial<AssetRecoveryMarker>
    if (
      typeof parsed?.release !== 'string' ||
      typeof parsed?.path !== 'string' ||
      typeof parsed?.at !== 'number' ||
      !Number.isFinite(parsed.at)
    ) {
      return null
    }
    return {
      release: parsed.release,
      path: parsed.path,
      at: parsed.at,
    }
  } catch {
    return null
  }
}

const writeRecoveryMarker = (marker: AssetRecoveryMarker) => {
  if (typeof window === 'undefined') {
    return
  }
  try {
    window.sessionStorage.setItem(ASSET_RECOVERY_STORAGE_KEY, JSON.stringify(marker))
  } catch {
    // Ignore storage errors.
  }
}

const isMarkerActive = (
  marker: AssetRecoveryMarker | null,
  { now = () => Date.now() }: AssetRecoveryStateOptions = {}
) => {
  if (!marker || typeof window === 'undefined') {
    return false
  }
  const release = getCurrentRelease()
  const path = window.location.pathname
  const timestamp = now()
  return (
    marker.release === release &&
    marker.path === path &&
    timestamp - marker.at < ASSET_RECOVERY_COOLDOWN_MS
  )
}

export const hasActiveAssetRecovery = (options: AssetRecoveryStateOptions = {}) =>
  isMarkerActive(readRecoveryMarker(), options)

const waitForAssetRecovery = async (): Promise<never> => await new Promise<never>(() => {})

const hasValidDefaultExport = <T extends ComponentType<any>>(
  module: { default?: T } | null | undefined
): module is { default: T } => Boolean(module && typeof module === 'object' && module.default)

const isMissingLazyDefaultExportError = (error: unknown) =>
  normalizeErrorMessage(error) === LAZY_MODULE_DEFAULT_EXPORT_ERROR

export const recoverFromAssetError = (
  error: unknown,
  { force = false, now = () => Date.now(), reload = () => window.location.reload() }: AssetRecoveryOptions = {}
) => {
  if (typeof window === 'undefined') {
    return false
  }
  if (!force && !isRecoverableAssetError(error)) {
    return false
  }

  const release = getCurrentRelease()
  const path = window.location.pathname
  const timestamp = now()
  const previousMarker = readRecoveryMarker()

  if (isMarkerActive(previousMarker, { now })) {
    return false
  }

  writeRecoveryMarker({
    release,
    path,
    at: timestamp,
  })
  reload()
  return true
}

export const installVitePreloadRecovery = () => {
  if (preloadRecoveryInstalled || typeof window === 'undefined') {
    return
  }
  preloadRecoveryInstalled = true

  window.addEventListener('vite:preloadError', ((event: Event) => {
    const preloadEvent = event as VitePreloadErrorEvent
    const recovered = recoverFromAssetError(preloadEvent.payload ?? preloadEvent, { force: true })
    if (recovered) {
      preloadEvent.preventDefault()
    }
  }) as (event: Event) => void)
}

export const lazyWithAssetRecovery = <T extends ComponentType<any>>(
  importer: () => Promise<{ default: T }>,
  recoveryOptions: Pick<AssetRecoveryOptions, 'reload' | 'now'> = {}
): LazyExoticComponent<T> =>
  lazy(async () => {
    try {
      const module = await importer()
      if (hasValidDefaultExport(module)) {
        return module
      }
      if (hasActiveAssetRecovery(recoveryOptions)) {
        return await waitForAssetRecovery()
      }
      throw new Error(LAZY_MODULE_DEFAULT_EXPORT_ERROR)
    } catch (error) {
      const recovered = recoverFromAssetError(error, recoveryOptions) || (
        isMissingLazyDefaultExportError(error)
          ? recoverFromAssetError(undefined, { ...recoveryOptions, force: true })
          : false
      )
      if (recovered || hasActiveAssetRecovery(recoveryOptions)) {
        return await waitForAssetRecovery()
      }
      throw error
    }
  })
